/**
 * Configuration management with environment detection and validation
 */

import * as dotenv from 'dotenv-expand';
import { z } from 'zod';
import {
  Config,
  ConfigSchema,
  CLIOptions,
  ConfigurationError,
  ValidationError,
  stringToBoolean,
} from '@/types';
import { EnvironmentUtils, EnvironmentType, CIProvider } from '@/utils/env';
import { ValidationUtils } from '@/utils/validation';
import { logger } from '@/utils/logger';

// Load environment variables with proper typing
dotenv.expand({ parsed: process.env as Record<string, string> });

/**
 * Default configuration values
 */
const DEFAULT_CONFIG = {
  // API URLs by environment
  API_URLS: {
    [EnvironmentType.PRODUCTION]: 'https://api.testdino.com',
    [EnvironmentType.STAGING]: 'https://staging-api.testdino.com',
    [EnvironmentType.DEVELOPMENT]: 'http://localhost:3000',
    [EnvironmentType.TEST]: 'http://localhost:3000',
  },

  // Default options
  UPLOAD_IMAGES: false,
  UPLOAD_VIDEOS: false,
  UPLOAD_HTML: false,
  UPLOAD_TRACES: false,
  UPLOAD_FILES: false,
  UPLOAD_FULL_JSON: false,
  VERBOSE: false,

  // Performance settings
  TIMEOUT_MS: 60000, // 60 seconds
  RETRY_COUNT: 3,
  MAX_FILE_SIZE_MB: 100,
  BATCH_SIZE: 5,
  MAX_CONCURRENT_UPLOADS: 10,
  UPLOAD_TIMEOUT: 60000,
} as const;

/**
 * Environment detection utilities
 */
class EnvironmentDetectorClass {
  /**
   * Determine current environment type
   */
  getEnvironmentType(): EnvironmentType {
    return EnvironmentUtils.detectEnvironmentType();
  }

  /**
   * Determine if we're in development mode
   */
  isDevelopment(): boolean {
    return EnvironmentUtils.isDevelopment();
  }

  /**
   * Determine if we're in production mode
   */
  isProduction(): boolean {
    return EnvironmentUtils.isProduction();
  }

  /**
   * Determine if we're in test mode
   */
  isTest(): boolean {
    return EnvironmentUtils.isTest();
  }

  /**
   * Check if running in CI environment
   */
  isCI(): boolean {
    return EnvironmentUtils.isCI();
  }

  /**
   * Get CI provider information
   */
  getCIProvider(): CIProvider {
    return EnvironmentUtils.detectCIProvider();
  }

  /**
   * Get the appropriate API URL based on environment
   */
  getApiUrl(): string {
    const envType = this.getEnvironmentType();

    // Check for explicit override (always allow override)
    const override = EnvironmentUtils.getStringEnv('TESTDINO_API_URL');
    if (override) {
      return override;
    }

    // Use environment-specific default
    return DEFAULT_CONFIG.API_URLS[envType];
  }

  /**
   * Get environment summary for debugging
   */
  getEnvironmentInfo(): Record<string, unknown> {
    return {
      ...EnvironmentUtils.getEnvironmentSummary(),
      apiUrl: this.getApiUrl(),
      isCI: this.isCI(),
      ciProvider: this.getCIProvider(),
    };
  }
}

/**
 * Enhanced configuration loader and validator
 */
export class ConfigLoader {
  /**
   * Validate API token format and environment compatibility
   */
  private validateToken(token: string): void {
    ValidationUtils.validateApiToken(token);

    // Extract environment from token
    const tokenEnv = token.split('_')[1];
    EnvironmentDetector.getEnvironmentType();

    // Warn about environment mismatches in development
    if (EnvironmentDetector.isDevelopment() && tokenEnv !== 'development') {
      logger.warn(
        `Using ${tokenEnv} token in development environment. Consider using a development token for local testing.`
      );
    }
  }

  /**
   * Resolve configuration values with proper precedence
   */
  private resolveConfigValue<T>(
    cliValue: T | undefined,
    envKey: string,
    defaultValue: T,
    converter?: (value: string) => T
  ): T {
    // CLI option has highest precedence
    if (cliValue !== undefined) {
      return cliValue;
    }

    // Environment variable is second
    const envValue = EnvironmentUtils.getStringEnv(
      envKey as keyof typeof EnvironmentUtils.getStringEnv
    );
    if (envValue !== undefined) {
      if (converter) {
        return converter(envValue);
      }
      // For boolean values
      if (typeof defaultValue === 'boolean') {
        return stringToBoolean(envValue) as T;
      }
      return envValue as T;
    }

    // Default value is last
    return defaultValue;
  }

  /**
   * Create configuration from CLI options with environment fallbacks
   */
  createConfig(options: CLIOptions): Config {
    // Token resolution: CLI > ENV > error
    const token = this.resolveConfigValue(options.token, 'TESTDINO_TOKEN', '');

    if (!token) {
      throw new ConfigurationError(
        'API token is required. Provide via --token flag or TESTDINO_TOKEN environment variable.'
      );
    }

    // Validate token format
    this.validateToken(token);

    // Build configuration with only CLI options (no environment variable fallbacks)
    const config: Config = {
      apiUrl: EnvironmentDetector.getApiUrl(),
      token,
      uploadImages: options.uploadImages || DEFAULT_CONFIG.UPLOAD_IMAGES,
      uploadVideos: options.uploadVideos || DEFAULT_CONFIG.UPLOAD_VIDEOS,
      uploadHtml: options.uploadHtml || DEFAULT_CONFIG.UPLOAD_HTML,
      uploadTraces: options.uploadTraces || DEFAULT_CONFIG.UPLOAD_TRACES,
      uploadFiles: options.uploadFiles || DEFAULT_CONFIG.UPLOAD_FILES,
      uploadFullJson: options.uploadFullJson || DEFAULT_CONFIG.UPLOAD_FULL_JSON,
      verbose: options.verbose || DEFAULT_CONFIG.VERBOSE,
      // Target environment tag (already resolved with env var fallback in commands.ts)
      environment: options.environment || 'unknown',
      // Run tags (already resolved with env var fallback in commands.ts)
      tags: options.tags || [],
      // Performance settings use defaults only
      batchSize: DEFAULT_CONFIG.BATCH_SIZE,
      maxConcurrentUploads: DEFAULT_CONFIG.MAX_CONCURRENT_UPLOADS,
      uploadTimeout: DEFAULT_CONFIG.UPLOAD_TIMEOUT,
      retryAttempts: DEFAULT_CONFIG.RETRY_COUNT,
    };

    // Validate the final configuration
    try {
      const validatedConfig = ConfigSchema.parse(config);

      // Log configuration summary in verbose mode
      if (validatedConfig.verbose) {
        this.logConfigurationSummary(validatedConfig, options);
      }

      return validatedConfig;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const issues = error.issues.map(
          issue => `${issue.path.join('.')}: ${issue.message}`
        );
        throw new ConfigurationError(
          `Invalid configuration: ${issues.join(', ')}`,
          error
        );
      }
      throw new ConfigurationError(
        'Configuration validation failed',
        error as Error
      );
    }
  }

  /**
   * Log configuration summary for debugging
   */
  private logConfigurationSummary(config: Config, options: CLIOptions): void {
    const envInfo = EnvironmentDetector.getEnvironmentInfo();

    logger.section('Configuration:');
    logger.kv('Environment', String(envInfo.type));
    logger.kv('CI/CD', envInfo.isCI ? `Yes (${envInfo.ciProvider})` : 'No');
    logger.kv('Report', options.reportDirectory);

    // Build upload flags summary
    const uploadFlags: string[] = [];
    if (config.uploadFullJson) uploadFlags.push('full-json');
    if (config.uploadImages) uploadFlags.push('images');
    if (config.uploadVideos) uploadFlags.push('videos');
    if (config.uploadHtml) uploadFlags.push('html');
    if (config.uploadTraces) uploadFlags.push('traces');
    if (config.uploadFiles) uploadFlags.push('files');

    if (uploadFlags.length > 0) {
      logger.kv('Upload', uploadFlags.join(', '));
    } else {
      logger.kv('Upload', 'report only');
    }

    // Show run tags if specified
    if (config.tags && config.tags.length > 0) {
      logger.kv('Run Tags', config.tags.join(', '));
    }
  }

  /**
   * Get runtime configuration with validation
   */
  getRuntimeConfig(): {
    timeout: number;
    retryCount: number;
    maxFileSize: number;
    logLevel: string;
  } {
    // Use only default values - no environment variable overrides
    const timeout = DEFAULT_CONFIG.TIMEOUT_MS;
    const retryCount = DEFAULT_CONFIG.RETRY_COUNT;
    const maxFileSizeMB = DEFAULT_CONFIG.MAX_FILE_SIZE_MB;
    const logLevel = 'info';

    // Validate runtime values
    ValidationUtils.validateTimeout(timeout);
    ValidationUtils.validateRetryCount(retryCount);

    return {
      timeout,
      retryCount,
      maxFileSize: maxFileSizeMB * 1024 * 1024, // Convert MB to bytes
      logLevel,
    };
  }

  /**
   * Validate environment for CLI operation
   */
  validateEnvironment(): void {
    try {
      // Validate environment variables
      EnvironmentUtils.getEnvironment();

      // Check Node.js version
      ValidationUtils.validateNodeVersion('18.0.0');

      // Validate API URL if present
      const apiUrl = EnvironmentDetector.getApiUrl();
      ValidationUtils.validateUrl(apiUrl, 'API');
    } catch (error) {
      if (
        error instanceof ConfigurationError ||
        error instanceof ValidationError
      ) {
        throw error;
      }
      throw new ConfigurationError(
        'Environment validation failed',
        error as Error
      );
    }
  }
}

/**
 * Global configuration instance
 */
export const configLoader = new ConfigLoader();

/**
 * Create a singleton instance of EnvironmentDetector
 */
export const EnvironmentDetector = new EnvironmentDetectorClass();

/**
 * Export utilities for external use
 */
export { EnvironmentUtils };
export type { Config, EnvironmentType, CIProvider };
